import React, { useState } from 'react';
import { ChevronDown, HelpCircle, ShoppingBag, Truck, CreditCard, Award, RotateCcw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './FAQPage.css';

const faqs = [
  {
    category: 'Orders',
    icon: ShoppingBag,
    items: [
      { q: 'How do I place an order?', a: 'Browse our shop, add items to your cart, and proceed to checkout. You\'ll need to be logged in to complete your purchase. We accept Paystack, MTN Mobile Money, Telecel Cash, and Airtel Money.' },
      { q: 'Can I modify my order after placing it?', a: 'Once an order is confirmed, modifications are not accepted. Please review your cart carefully before checkout. If you have an urgent issue, contact us at orders@auroragh.com immediately.' },
      { q: 'How will I know my order is confirmed?', a: 'You will receive an email confirmation with your order details once your payment is processed successfully.' },
      { q: 'What happens if a product is out of stock?', a: 'If a product becomes unavailable after your order, we will contact you immediately to offer an alternative or a full refund.' },
    ]
  },
  {
    category: 'Delivery',
    icon: Truck,
    items: [
      { q: 'What are your delivery areas?', a: 'We offer free delivery within the Greater Accra region. Orders outside Greater Accra are delivered via courier, with shipping costs paid by the customer at reception.' },
      { q: 'How long does delivery take?', a: 'Delivery takes between 1 and 7 working days depending on product availability, your address, and when the order was placed. Same-day delivery may be available in Accra.' },
      { q: 'How do I track my order?', a: 'You can track your order in real time from your account dashboard under "My Orders". You will also receive status updates via email.' },
      { q: 'What if my delivery is late?', a: 'If your order hasn\'t arrived within the expected timeframe, contact us at orders@auroragh.com or call 0550866710 and we\'ll look into it right away.' },
    ]
  },
  {
    category: 'Payments',
    icon: CreditCard,
    items: [
      { q: 'What payment methods do you accept?', a: 'We accept Paystack (card payments), MTN Mobile Money, Telecel Cash, and Airtel Money. All transactions are secure.' },
      { q: 'Is online payment safe?', a: 'Absolutely. We use Paystack, a PCI-DSS compliant payment gateway. Your card details are never stored on our servers.' },
      { q: 'Can I pay on delivery?', a: 'Currently, all payments must be completed online before delivery. We do not accept cash on delivery.' },
      { q: 'What do I do if my payment fails?', a: 'Try again with a different payment method or contact your bank. If the issue persists, email us at sales@auroragh.com with your order details.' },
    ]
  },
  {
    category: 'Loyalty Program',
    icon: Award,
    items: [
      { q: 'How do I earn Aurora Points?', a: 'You earn Aurora Points automatically on every purchase. GHS 1 spent = 1 Aurora Point. Points are credited to your wallet after order delivery.' },
      { q: 'How do I redeem my points?', a: 'At checkout, you can choose to redeem your Aurora Points. 100 Points = GHS 1 discount. You can redeem any amount up to your available balance.' },
      { q: 'What are the loyalty tiers?', a: 'We have four customer tiers: Shell (Economy), Silverfin (Business), Goldfin (First), and Blue Marlin (Prime). Each tier unlocks better rewards and benefits based on your XP.' },
      { q: 'What are loyalty cards?', a: 'Loyalty cards give you a fixed discount (5%–20%) on all purchases for a set period. Cards can be purchased from your dashboard under "My Wallet".' },
      { q: 'Do my points expire?', a: 'Aurora Points do not expire as long as your account remains active. We recommend logging in at least once every 12 months.' },
    ]
  },
  {
    category: 'Returns & Refunds',
    icon: RotateCcw,
    items: [
      { q: 'Can I return a product?', a: 'Once an order is confirmed, cancellations and returns are not accepted for food safety reasons. If your product arrives in an unsuitable condition, contact us with photos and we will replace it.' },
      { q: 'My product arrived spoiled. What do I do?', a: 'Contact us immediately at sales@auroragh.com or 0550866710 with your order number and photos of the product. We will arrange a replacement at no cost.' },
      { q: 'How long do refunds take?', a: 'If a refund is approved (e.g., out-of-stock product), it will be processed within 5–10 business days back to your original payment method.' },
    ]
  },
];

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('Orders');

  const toggle = (cat, idx) => {
    const key = `${cat}-${idx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const activeFaqs = faqs.find(f => f.category === activeCategory);

  return (
    <>
      <Navbar />
      <div className="faq-page">

        <section className="faq-hero">
          <div className="container">
            <div className="faq-hero-badge"><HelpCircle size={14} /> Frequently Asked Questions</div>
            <h1 className="faq-hero-title">Got <span>Questions?</span><br />We Have Answers.</h1>
            <p className="faq-hero-sub">Everything you need to know about Aurora Seafood. Can't find your answer? <a href="/contact">Contact us</a>.</p>
          </div>
        </section>

        <section className="faq-body">
          <div className="container">
            <div className="faq-layout">

              <div className="faq-sidebar">
                {faqs.map(f => {
                  const Icon = f.icon;
                  return (
                    <button
                      key={f.category}
                      className={`faq-cat-btn ${activeCategory === f.category ? 'active' : ''}`}
                      onClick={() => setActiveCategory(f.category)}
                    >
                      <Icon size={18} />
                      {f.category}
                    </button>
                  );
                })}
                <div className="faq-sidebar-cta">
                  <p>Still have questions?</p>
                  <a href="/contact" className="faq-contact-btn">Contact Us</a>
                </div>
              </div>

              <div className="faq-content">
                <h2 className="faq-category-title">{activeCategory}</h2>
                <div className="faq-list">
                  {activeFaqs?.items.map((item, idx) => {
                    const key = `${activeCategory}-${idx}`;
                    const isOpen = !!openItems[key];
                    return (
                      <div key={idx} className={`faq-item ${isOpen ? 'open' : ''}`}>
                        <button className="faq-question" onClick={() => toggle(activeCategory, idx)}>
                          <span>{item.q}</span>
                          <ChevronDown size={18} className="faq-chevron" />
                        </button>
                        {isOpen && <div className="faq-answer"><p>{item.a}</p></div>}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
};

export default FAQPage;